import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';
import { Button } from '../components/ui';

function fmt(n) {
  return new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
}
function fmtDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('tr-TR');
}

const STATUS_LABELS = {
  accrued: { label: 'Tahakkuk', color: '#16a34a', bg: 'rgba(22,163,74,0.12)' },
  pending_collection: { label: 'Tahsilat Bekliyor', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
  paid: { label: 'Ödendi', color: '#0284c7', bg: 'rgba(2,132,199,0.12)' },
  cancelled: { label: 'İptal', color: '#dc2626', bg: 'rgba(220,38,38,0.12)' },
};

function StatusBadge({ status }) {
  const c = STATUS_LABELS[status] || STATUS_LABELS.accrued;
  return <span style={{ background: c.bg, color: c.color, padding: '2px 7px', borderRadius: 10, fontSize: '0.72rem', fontWeight: 600 }}>{c.label}</span>;
}

export default function CommissionReportPage() {
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [from, setFrom] = useState(firstOfMonth.toISOString().slice(0, 10));
  const [to, setTo] = useState(today.toISOString().slice(0, 10));
  const [personnelId, setPersonnelId] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [summary, setSummary] = useState([]);
  const [entries, setEntries] = useState([]);
  const [personnelList, setPersonnelList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [msg, setMsg] = useState(null);
  const [bonusModalOpen, setBonusModalOpen] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = { from, to };
      if (personnelId) params.personnel_id = personnelId;
      const detailParams = { ...params };
      if (statusFilter) detailParams.status = statusFilter;
      const [s, e] = await Promise.all([
        api.get('/commissions/summary', { params }),
        api.get('/commissions', { params: detailParams }),
      ]);
      if (s.data.success) setSummary(s.data.data || []);
      if (e.data.success) setEntries(e.data.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [from, to, personnelId, statusFilter]);

  const fetchPersonnel = useCallback(async () => {
    try {
      const r = await api.get('/personnel', { params: { limit: 200 } });
      if (r.data.success) {
        setPersonnelList((r.data.data.items || []).filter(p => p.commission_type));
      }
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { fetchPersonnel(); }, [fetchPersonnel]);

  const totalAccrued = useMemo(() => summary.reduce((s, x) => s + parseFloat(x.accrued_total || 0), 0), [summary]);
  const totalPending = useMemo(() => summary.reduce((s, x) => s + parseFloat(x.pending_total || 0), 0), [summary]);
  const totalPaid = useMemo(() => summary.reduce((s, x) => s + parseFloat(x.paid_total || 0), 0), [summary]);

  const toggleSelect = (id) => {
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  // Seçilebilir: accrued + paid (paid'leri geri almak için de seçim gerek)
  const accruedSelectableIds = useMemo(() =>
    entries.filter(e => e.status === 'accrued' || e.status === 'paid').map(e => e.id),
    [entries]
  );

  const allAccruedSelected = accruedSelectableIds.length > 0 && accruedSelectableIds.every(id => selected.has(id));
  const toggleSelectAll = () => {
    if (allAccruedSelected) setSelected(new Set());
    else setSelected(new Set(accruedSelectableIds));
  };

  const markSelectedPaid = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`${selected.size} kayıt "ödendi" olarak işaretlensin mi?`)) return;
    try {
      const r = await api.post('/commissions/mark-paid', { entry_ids: Array.from(selected), paid_date: new Date().toISOString().slice(0,10) });
      if (r.data.success) {
        setMsg({ type: 'success', text: `${r.data.updated} kayıt ödendi olarak işaretlendi` });
        setSelected(new Set());
        fetchAll();
        setTimeout(() => setMsg(null), 4000);
      }
    } catch (e) { setMsg({ type: 'error', text: e.response?.data?.message || e.message }); }
  };

  // Ödendi işaretini geri al — paid → accrued
  const unmarkSelectedPaid = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`${selected.size} kaydın "ödendi" işareti geri alınsın mı? (Tekrar tahakkuk listesine düşer)`)) return;
    try {
      const r = await api.post('/commissions/unmark-paid', { entry_ids: Array.from(selected) });
      if (r.data.success) {
        setMsg({ type: 'success', text: `${r.data.updated} kayıt tahakkuk durumuna geri alındı` });
        setSelected(new Set());
        fetchAll();
        setTimeout(() => setMsg(null), 4000);
      }
    } catch (e) { setMsg({ type: 'error', text: e.response?.data?.message || e.message }); }
  };

  // Tier bonus iptali — kayıt sil + personel hareketinden de düş
  const cancelSelected = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`⚠ ${selected.size} tier bonus kaydı tamamen SİLİNECEK ve personel hareketinden de düşecek.\nDevam edilsin mi?`)) return;
    try {
      const r = await api.post('/commissions/cancel', { entry_ids: Array.from(selected) });
      if (r.data.success) {
        setMsg({ type: 'success', text: `${r.data.cancelled} kayıt iptal edildi (personel hareketinden de düşürüldü)` });
        setSelected(new Set());
        fetchAll();
        setTimeout(() => setMsg(null), 4000);
      }
    } catch (e) { setMsg({ type: 'error', text: e.response?.data?.message || e.message }); }
  };

  return (
    <div style={{ padding: 16 }}>
      {/* Filtreler */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-secondary)', borderRadius: 12, padding: 14, marginBottom: 12, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
        <div>
          <label style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>Başlangıç</label>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={{ width: '100%', padding: '7px 10px', border: '1px solid var(--border-input)', borderRadius: 6, background: 'var(--bg-input)', color: 'var(--text-primary)' }}/>
        </div>
        <div>
          <label style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>Bitiş</label>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} style={{ width: '100%', padding: '7px 10px', border: '1px solid var(--border-input)', borderRadius: 6, background: 'var(--bg-input)', color: 'var(--text-primary)' }}/>
        </div>
        <div>
          <label style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>Personel</label>
          <select value={personnelId} onChange={e => setPersonnelId(e.target.value)} style={{ width: '100%', padding: '7px 10px', border: '1px solid var(--border-input)', borderRadius: 6, background: 'var(--bg-input)', color: 'var(--text-primary)' }}>
            <option value="">Tümü</option>
            {personnelList.map(p => <option key={p.id} value={p.id}>{p.code} — {p.full_name}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>Durum</label>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ width: '100%', padding: '7px 10px', border: '1px solid var(--border-input)', borderRadius: 6, background: 'var(--bg-input)', color: 'var(--text-primary)' }}>
            <option value="">Tümü</option>
            <option value="accrued">Tahakkuk</option>
            <option value="pending_collection">Tahsilat Bekliyor</option>
            <option value="paid">Ödendi</option>
            <option value="cancelled">İptal</option>
          </select>
        </div>
      </div>

      {msg && <div className={'detail-msg ' + msg.type} style={{ marginBottom: 10 }}>{msg.text}</div>}

      {/* Toplam kartları */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10, marginBottom: 12 }}>
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-secondary)', borderRadius: 12, padding: 14 }}>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)' }}>Tahakkuk Toplam</div>
          <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#16a34a' }}>{fmt(totalAccrued)} TRY</div>
        </div>
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-secondary)', borderRadius: 12, padding: 14 }}>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)' }}>Tahsilat Bekliyor</div>
          <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#f59e0b' }}>{fmt(totalPending)} TRY</div>
        </div>
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-secondary)', borderRadius: 12, padding: 14 }}>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)' }}>Ödenen Toplam</div>
          <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#0284c7' }}>{fmt(totalPaid)} TRY</div>
        </div>
      </div>

      {/* Dönem Bonusu Hesapla butonu */}
      <div style={{ marginBottom: 12, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Button variant="primary" onClick={() => setBonusModalOpen(true)}>🎯 Dönem Bonusu Hesapla (Tier)</Button>
      </div>

      {bonusModalOpen && <BonusModal from={from} to={to} onClose={() => setBonusModalOpen(false)} onApplied={() => { setBonusModalOpen(false); fetchAll(); }} />}

      {/* Personel × dönem özeti */}
      {summary.length > 0 && (
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-secondary)', borderRadius: 12, marginBottom: 12, overflow: 'hidden' }}>
          <div style={{ padding: 10, borderBottom: '1px solid var(--border-secondary)', fontWeight: 600, fontSize: '0.9rem' }}>
            Personel Özeti
          </div>
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table style={{ width: '100%', minWidth: 640, borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead style={{ background: 'var(--bg-secondary)' }}>
                <tr>
                  <th style={{ padding: '8px 10px', textAlign: 'left', whiteSpace: 'nowrap' }}>Personel</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', whiteSpace: 'nowrap' }}>Tip</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>Matrah</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>Tahakkuk</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>Bekleyen</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right' }}>Ödenen</th>
                  <th style={{ padding: '8px 10px', textAlign: 'center' }}>Kayıt</th>
                </tr>
              </thead>
              <tbody>
                {summary.map(s => (
                  <tr key={s.personnel_id} style={{ borderBottom: '1px solid var(--border-secondary)' }}>
                    <td style={{ padding: '7px 10px', whiteSpace: 'nowrap' }}>
                      <strong>{s.personnel_name}</strong> <small style={{ color: 'var(--text-tertiary)' }}>{s.personnel_code}</small>
                    </td>
                    <td style={{ padding: '7px 10px', fontSize: '0.78rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                      {s.commission_type === 'sale' ? 'Satış' : s.commission_type === 'collection' ? 'Tahsilat' : '—'}
                    </td>
                    <td style={{ padding: '7px 10px', textAlign: 'right', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{fmt(s.base_total)}</td>
                    <td style={{ padding: '7px 10px', textAlign: 'right', fontFamily: 'monospace', color: '#16a34a', fontWeight: 600, whiteSpace: 'nowrap' }}>{fmt(s.accrued_total)}</td>
                    <td style={{ padding: '7px 10px', textAlign: 'right', fontFamily: 'monospace', color: '#f59e0b', whiteSpace: 'nowrap' }}>{fmt(s.pending_total)}</td>
                    <td style={{ padding: '7px 10px', textAlign: 'right', fontFamily: 'monospace', color: '#0284c7', whiteSpace: 'nowrap' }}>{fmt(s.paid_total)}</td>
                    <td style={{ padding: '7px 10px', textAlign: 'center', whiteSpace: 'nowrap' }}>{s.entries_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detay liste */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-secondary)', borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ padding: 10, borderBottom: '1px solid var(--border-secondary)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>Prim Hareketleri ({entries.length})</span>
          {selected.size > 0 && (() => {
            const selectedEntries = entries.filter(e => selected.has(e.id));
            const hasTierBonus = selectedEntries.some(e => e.source_type === 'tier_bonus');
            return (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {hasTierBonus && (
                  <Button size="sm" variant="danger" onClick={cancelSelected}>🗑 Tahakkuku İptal Et ({selected.size})</Button>
                )}
              </div>
            );
          })()}
        </div>
        {loading ? (
          <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-tertiary)' }}>Yükleniyor...</div>
        ) : entries.length === 0 ? (
          <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-tertiary)' }}>Bu dönemde prim kaydı yok</div>
        ) : (
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table style={{ width: '100%', minWidth: 920, borderCollapse: 'collapse', fontSize: '0.82rem' }}>
              <thead style={{ background: 'var(--bg-secondary)' }}>
                <tr>
                  <th style={{ padding: '8px 6px', width: 28, textAlign: 'center' }}>
                    <input type="checkbox" checked={allAccruedSelected} onChange={toggleSelectAll} disabled={accruedSelectableIds.length === 0} title="Tahakkuk olanları seç"/>
                  </th>
                  <th style={{ padding: '8px 8px', textAlign: 'left', whiteSpace: 'nowrap' }}>Tarih</th>
                  <th style={{ padding: '8px 8px', textAlign: 'left', whiteSpace: 'nowrap' }}>Personel</th>
                  <th style={{ padding: '8px 8px', textAlign: 'left', whiteSpace: 'nowrap' }}>Cari</th>
                  <th style={{ padding: '8px 8px', textAlign: 'left', whiteSpace: 'nowrap' }}>Stok</th>
                  <th style={{ padding: '8px 8px', textAlign: 'left', whiteSpace: 'nowrap' }}>Belge</th>
                  <th style={{ padding: '8px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>Matrah</th>
                  <th style={{ padding: '8px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>%</th>
                  <th style={{ padding: '8px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>Prim</th>
                  <th style={{ padding: '8px 8px', textAlign: 'center', whiteSpace: 'nowrap' }}>Durum</th>
                </tr>
              </thead>
              <tbody>
                {entries.map(e => (
                  <tr key={e.id} style={{ borderBottom: '1px solid var(--border-secondary)' }}>
                    <td style={{ padding: '6px', textAlign: 'center' }}>
                      {(e.status === 'accrued' || e.status === 'paid') && (
                        <input type="checkbox" checked={selected.has(e.id)} onChange={() => toggleSelect(e.id)}/>
                      )}
                    </td>
                    <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{fmtDate(e.accrued_date || e.created_at)}</td>
                    <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{e.personnel_name}</td>
                    <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{e.account_title}</td>
                    <td style={{ padding: '6px 8px', fontSize: '0.78rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{e.stock_name || '—'}</td>
                    <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: '0.78rem', color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>{e.invoice_no || '—'}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{fmt(e.base_amount)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>%{fmt(e.commission_rate)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600, color: 'var(--accent-primary)', whiteSpace: 'nowrap' }}>{fmt(e.commission_amount)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'center', whiteSpace: 'nowrap' }}><StatusBadge status={e.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Dönem Bonusu Hesap Modal'ı (Yeni Yapı: Hedef Bazlı) ──────────────
function BonusModal({ from, to, onClose, onApplied }) {
  const [period_start, setPS] = useState(from);
  const [period_end, setPE] = useState(to);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [picked, setPicked] = useState(new Set());

  const calculate = async () => {
    setLoading(true);
    try {
      const r = await api.post('/commissions/calculate-bonuses', { period_start, period_end });
      if (r.data.success) {
        setData(r.data.data);
        if (r.data.data.enabled) {
          // Bonus üreten hedefleri otomatik seç
          setPicked(new Set((r.data.data.rows || []).filter(x => (x.total_bonus || 0) >= 0.01).map(x => x.target_id)));
        }
      }
    } catch (e) {
      alert(e.response?.data?.message || 'Hesaplama başarısız');
    } finally { setLoading(false); }
  };

  const apply = async () => {
    if (!data || picked.size === 0) return;
    if (!window.confirm(`${picked.size} hedef için bonus kaydı yazılacak. Onaylıyor musunuz?`)) return;
    setApplying(true);
    try {
      const r = await api.post('/commissions/apply-bonuses', {
        period_start, period_end,
        target_ids: Array.from(picked),
      });
      if (r.data.success) {
        alert(`${r.data.data.written} bonus kaydı yazıldı (personel × hedef).`);
        onApplied();
      }
    } catch (e) { alert(e.response?.data?.message || 'Uygulama hatası'); }
    finally { setApplying(false); }
  };

  const togglePick = (tid) => {
    setPicked(prev => { const n = new Set(prev); if (n.has(tid)) n.delete(tid); else n.add(tid); return n; });
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 20,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: 'var(--bg-card)', borderRadius: 12, width: 'min(1100px, 100%)',
        maxHeight: '90vh', display: 'flex', flexDirection: 'column',
      }}>
        <div style={{ padding: 14, borderBottom: '1px solid var(--border-secondary)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: '1.5rem' }}>🎯</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>Dönem Bonusu Hesapla</div>
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Hedef yüzdesine göre tier çarpanı + bonus önizleme</div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', fontSize: 22, cursor: 'pointer' }}>×</button>
        </div>

        <div style={{ padding: 14, borderBottom: '1px solid var(--border-secondary)', background: 'var(--bg-secondary)', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Dönem Başlangıç</label>
            <input type="date" value={period_start} onChange={e => setPS(e.target.value)}
              style={{ display: 'block', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border-input)', background: 'var(--bg-input)', color: 'var(--text-primary)' }} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Dönem Bitiş</label>
            <input type="date" value={period_end} onChange={e => setPE(e.target.value)}
              style={{ display: 'block', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border-input)', background: 'var(--bg-input)', color: 'var(--text-primary)' }} />
          </div>
          <Button onClick={calculate} disabled={loading}>{loading ? 'Hesaplanıyor...' : '🔍 Hesapla'}</Button>
        </div>

        <div style={{ padding: 14, overflow: 'auto', flex: 1 }}>
          {!data && (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-tertiary)' }}>
              Dönem aralığı seçip <strong>"Hesapla"</strong> ile önizle.
            </div>
          )}
          {data && !data.enabled && (
            <div style={{ padding: 14, borderRadius: 10, background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.35)', color: '#b45309' }}>
              ⚠ Tier sistemi <strong>kapalı</strong>. Önce <strong>Ayarlar → Prim Kademeleri</strong> sayfasından açın.
            </div>
          )}
          {data && data.enabled && data.rows.length === 0 && (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-tertiary)' }}>
              Bu dönemde aktif satış hedefi yok. Önce <strong>Satış Hedefleri</strong> sayfasından hedef tanımlayın.
            </div>
          )}
          {data && data.enabled && data.rows.length > 0 && (
            <>
              <div style={{ marginBottom: 10, padding: 10, borderRadius: 8, background: 'var(--bg-secondary)', fontSize: 12, color: 'var(--text-secondary)' }}>
                <strong>Tier Tablosu:</strong>{' '}
                {data.tiers.map((t, i) => (
                  <span key={i} style={{ marginRight: 12 }}>
                    %{t.min_pct}+ → {t.type === 'fixed' ? fmt(t.value) + ' TL sabit' : '%' + t.value + ' (hedef üstü ciro)'}
                  </span>
                ))}
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead style={{ background: 'var(--bg-secondary)' }}>
                  <tr>
                    <th style={mth}><input type="checkbox" checked={picked.size === data.rows.filter(r => (r.total_bonus || 0) >= 0.01).length && picked.size > 0} onChange={() => {
                      const elig = data.rows.filter(r => (r.total_bonus || 0) >= 0.01).map(r => r.target_id);
                      if (picked.size === elig.length) setPicked(new Set()); else setPicked(new Set(elig));
                    }} /></th>
                    <th style={{ ...mth, textAlign: 'left' }}>Hedef Kapsamı</th>
                    <th style={mth}>Hedef</th>
                    <th style={mth}>Gerçekleşen</th>
                    <th style={mth}>%</th>
                    <th style={{ ...mth, textAlign: 'left' }}>Tier</th>
                    <th style={mth}>Toplam Bonus</th>
                    <th style={{ ...mth, textAlign: 'left' }}>Dağıtım</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map(r => {
                    const eligible = (r.total_bonus || 0) >= 0.01;
                    const checked = picked.has(r.target_id);
                    return (
                      <tr key={r.target_id} style={{ borderTop: '1px solid var(--border-secondary)', opacity: eligible ? 1 : 0.55, background: r.warning ? 'rgba(245,158,11,0.06)' : 'transparent' }}>
                        <td style={mtd}><input type="checkbox" checked={checked} disabled={!eligible} onChange={() => togglePick(r.target_id)} /></td>
                        <td style={{ ...mtd, textAlign: 'left', fontWeight: 600 }}>
                          {r.scope_label}
                          {r.warning && <div style={{ fontSize: 10, color: '#b45309', marginTop: 2 }}>⚠ {r.warning}</div>}
                        </td>
                        <td style={{ ...mtd, fontFamily: 'monospace' }}>{fmt(r.target_amount)} {r.currency_code}</td>
                        <td style={{ ...mtd, fontFamily: 'monospace' }}>{fmt(r.achieved_amount)}</td>
                        <td style={{ ...mtd, fontFamily: 'monospace', fontWeight: 700, color: r.achievement_pct == null ? 'var(--text-tertiary)' : r.achievement_pct >= 100 ? '#10b981' : '#dc2626' }}>
                          {r.achievement_pct != null ? '%' + r.achievement_pct.toFixed(1) : '—'}
                        </td>
                        <td style={{ ...mtd, textAlign: 'left', fontSize: 11 }}>
                          {r.tier ? (
                            <span>%{r.tier.min_pct}+ {r.tier.type === 'fixed' ? '· sabit ' + fmt(r.tier.value) + ' TL' : '· %' + r.tier.value + ' (hedef üstü)'}</span>
                          ) : '—'}
                        </td>
                        <td style={{ ...mtd, fontFamily: 'monospace', fontWeight: 800, color: (r.total_bonus || 0) > 0 ? '#10b981' : 'var(--text-tertiary)' }}>
                          {fmt(r.total_bonus)} TL
                        </td>
                        <td style={{ ...mtd, textAlign: 'left', fontSize: 11 }}>
                          {(r.distributions || []).filter(d => d.bonus_share > 0).map(d => (
                            <div key={d.personnel_id}>
                              {d.personnel_name} <span style={{ color: 'var(--text-tertiary)' }}>(%{d.dist_share})</span> → <strong>{fmt(d.bonus_share)}</strong>
                            </div>
                          ))}
                          {(r.distributions || []).filter(d => d.bonus_share > 0).length === 0 && <span style={{ color: 'var(--text-tertiary)' }}>—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}
        </div>

        <div style={{ padding: 14, borderTop: '1px solid var(--border-secondary)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
          <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
            {picked.size > 0 && <span><strong>{picked.size}</strong> hedef için bonus yazılacak</span>}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="ghost" onClick={onClose}>Kapat</Button>
            <Button onClick={apply} disabled={!data?.enabled || picked.size === 0 || applying}>
              {applying ? 'Uygulanıyor...' : '✓ Seçili Hedeflere Bonus Yaz'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

const mth = { padding: '8px 10px', fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.4, textAlign: 'right' };
const mtd = { padding: '8px 10px', textAlign: 'right', whiteSpace: 'nowrap' };
